import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { TruckIcon, AlertCircleIcon, CheckCircleIcon, ClockIcon, MessageSquareIcon, PinIcon, Edit2Icon, Trash2Icon, CheckIcon, XIcon, SaveIcon, InfoIcon } from 'lucide-react';
import { useDarkClasses } from '../../../hooks/useDarkClasses';
import { useRoleTheme } from '../../../hooks/useRoleTheme';
import { VendorUpdate, VendorFulfillmentSettings, mockVendorFulfillmentSettings, mockFulfillmentData, mockVendorUpdates, DelayStatus, CommunicationStatus, VendorCategory, getCategoryConfig } from '../../../data/fulfillmentData';
import { FulfillmentTransparencyCard } from '../../../components/Vendors/FulfillmentTransparencyCard';
interface VendorFulfillmentDashboardProps {
  vendorCategory?: VendorCategory;
}
export const VendorFulfillmentDashboard: React.FC<VendorFulfillmentDashboardProps> = ({
  vendorCategory = 'merch'
}) => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const config = getCategoryConfig(vendorCategory);
  // State for settings form
  const [settings, setSettings] = useState<VendorFulfillmentSettings>(mockVendorFulfillmentSettings['1'] || {
    averageProductionTime: '',
    currentCustomerExpectation: '',
    shippingOrigin: '',
    subscriptionTiming: '',
    queueStatus: 'open',
    queueCapacity: '',
    bookingWindow: ''
  });
  const [savedSettings, setSavedSettings] = useState<VendorFulfillmentSettings>(settings);
  const isDirty = JSON.stringify(settings) !== JSON.stringify(savedSettings);
  // State for updates
  const [updates, setUpdates] = useState<VendorUpdate[]>(mockVendorUpdates['1'] || []);
  // State for composer modal
  const [isComposerOpen, setIsComposerOpen] = useState(false);
  const [composerData, setComposerData] = useState<Partial<VendorUpdate>>({
    type: 'production-update',
    message: '',
    estimatedNewTimeline: '',
    affectedScope: '',
    is_pinned: false
  });
  const [editingUpdateId, setEditingUpdateId] = useState<string | null>(null);
  // Derived public status (mock logic for demo)
  const currentPublicStatus = mockFulfillmentData['1'];
  const handleSaveSettings = () => {
    setSavedSettings(settings);
    toast.success('Fulfillment settings saved successfully');
  };
  const handleOpenComposer = (update?: VendorUpdate, isResolve: boolean = false) => {
    if (isResolve && update) {
      setComposerData({
        type: 'resolved',
        message: `Resolved: ${update.message.substring(0, 50)}...`,
        affectedScope: update.affectedScope,
        resolved_from_update_id: update.id
      });
      setEditingUpdateId(null);
    } else if (update) {
      setComposerData(update);
      setEditingUpdateId(update.id);
    } else {
      setComposerData({
        type: 'production-update',
        message: '',
        estimatedNewTimeline: '',
        affectedScope: '',
        is_pinned: false
      });
      setEditingUpdateId(null);
    }
    setIsComposerOpen(true);
  };
  const handleSaveUpdate = () => {
    if (!composerData.message || !composerData.affectedScope) {
      toast.error('Message and Affected Scope are required');
      return;
    }
    const newUpdate: VendorUpdate = {
      id: editingUpdateId || `u${Date.now()}`,
      type: composerData.type as VendorUpdate['type'],
      message: composerData.message,
      estimatedNewTimeline: composerData.estimatedNewTimeline,
      datePosted: editingUpdateId ? updates.find(u => u.id === editingUpdateId)?.datePosted || new Date().toISOString() : new Date().toISOString(),
      affectedScope: composerData.affectedScope,
      is_pinned: composerData.is_pinned,
      is_edited: !!editingUpdateId,
      edited_at: editingUpdateId ? new Date().toISOString() : undefined,
      resolved_from_update_id: composerData.resolved_from_update_id
    };
    setUpdates(prev => {
      let next = [...prev];
      if (newUpdate.is_pinned) {
        next = next.map(u => ({
          ...u,
          is_pinned: false
        }));
      }
      if (editingUpdateId) {
        return next.map(u => u.id === editingUpdateId ? newUpdate : u);
      }
      return [newUpdate, ...next];
    });
    toast.success(editingUpdateId ? 'Update saved' : 'Update posted');
    setIsComposerOpen(false);
  };
  const handleDeleteUpdate = (id: string) => {
    if (window.confirm('Are you sure you want to delete this update?')) {
      setUpdates(prev => prev.map(u => u.id === id ? {
        ...u,
        is_deleted_soft: true
      } : u));
      toast.success('Update deleted');
    }
  };
  const visibleUpdates = updates.filter(u => !u.is_deleted_soft).sort((a, b) => {
    if (a.is_pinned && !b.is_pinned) return -1;
    if (!a.is_pinned && b.is_pinned) return 1;
    return new Date(b.datePosted).getTime() - new Date(a.datePosted).getTime();
  });
  const getTypeColor = (type: string) => {
    switch (type) {
      case 'delay':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'issue':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'resolved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'shipping-update':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'delay':
        return <AlertCircleIcon className="h-4 w-4" data-id="element-18818" data-mp="1qcy32vep4pzf" />;
      case 'issue':
        return <AlertCircleIcon className="h-4 w-4" data-id="element-18819" data-mp="1mcxmb444k2p7" />;
      case 'resolved':
        return <CheckCircleIcon className="h-4 w-4" data-id="element-18820" data-mp="2iuaclou09syu" />;
      case 'shipping-update':
        return <TruckIcon className="h-4 w-4" data-id="element-18821" data-mp="2iwert2xcueef" />;
      default:
        return <InfoIcon className="h-4 w-4" data-id="element-18822" data-mp="2e2sbn0rzfsdv" />;
    }
  };
  const formatType = (type: string) => {
    return type.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };
  return <div className="space-y-8" data-id="element-18823" data-mp="h4lbkfz7dt0x">
      {/* Current Public Status Card */}
      <section data-id="element-18824" data-mp="2h07ojui7pry">
        <div className="flex items-center gap-2 mb-4" data-id="element-18825" data-mp="2fgsyxohxmsa">
          <h2 className={`text-lg font-bold ${dk.textHeading}`} data-id="element-18826" data-mp="7f1qd3o0pn2w">
            What Customers See
          </h2>
          <div className={`flex items-center text-xs px-2 py-1 rounded-full ${dk.isDark ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-50 text-blue-600'}`} data-id="element-18827" data-mp="3pkeq4p9z4ufc">
            <InfoIcon className="h-3 w-3 mr-1" data-id="element-18828" data-mp="3fmryr3bhnaeh" />
            Public Status
          </div>
        </div>
        <div className={`p-4 rounded-xl border ${dk.card} flex flex-col md:flex-row gap-4 items-start md:items-center`} data-id="element-18829" data-mp="3klv64uexxspm">
          <div className="flex-1" data-id="element-18830" data-mp="1dwgftds2r9ex">
            <div className="flex items-center gap-2 mb-2" data-id="element-18831" data-mp="1dtaqbxfja5io">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${currentPublicStatus.delayStatus === 'on-time' ? 'bg-green-100 text-green-800' : currentPublicStatus.delayStatus === 'delayed' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`} data-id="element-18832" data-mp="1itfbi0rdzof8">
                {currentPublicStatus.delayStatus === 'on-time' ? 'On Time' : currentPublicStatus.delayStatus === 'delayed' ? 'Delayed' : 'Significant Delay'}
              </span>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${currentPublicStatus.communicationStatus === 'active' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-18833" data-mp="3i26uj89vbwbp">
                {currentPublicStatus.communicationStatus === 'active' ? 'Actively Communicating' : 'Inactive'}
              </span>
            </div>
            <p className={`text-sm ${dk.textSecondary}`} data-id="element-18834" data-mp="1wpx1ds8uzg5h">
              Based on your settings and recent updates, this is your current
              public fulfillment status.
            </p>
          </div>
          <div className="shrink-0" data-id="element-18835" data-mp="1frwwnyt4c6lo">
            <button onClick={() => window.scrollTo({
            top: document.getElementById('settings-form')?.offsetTop,
            behavior: 'smooth'
          })} className={`text-sm font-medium ${rt.accentLink}`} data-id="element-18836" data-mp="1kk6ip3r44mio">
              Edit Settings →
            </button>
          </div>
        </div>
      </section>

      {/* Fulfillment Settings Form & Live Preview */}
      <section id="settings-form" data-id="element-18837" data-mp="11wbb61m3g4y0">
        <h2 className={`text-lg font-bold mb-4 ${dk.textHeading}`} data-id="element-18838" data-mp="16t1o4g2lqkf7">
          {config.sectionTitle}
        </h2>

        <AnimatePresence data-id="element-18839" data-mp="2da6p5719i7nn">
          {isDirty && <motion.div initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} className={`mb-4 p-3 rounded-lg border flex items-center justify-between ${dk.isDark ? 'bg-yellow-900/20 border-yellow-700/50' : 'bg-yellow-50 border-yellow-200'}`} data-id="element-18840" data-mp="2dbhx7j1qq1kk">
              <div className="flex items-center text-yellow-800 dark:text-yellow-500 text-sm font-medium" data-id="element-18841" data-mp="3nziixfjfs34o">
                <AlertCircleIcon className="h-4 w-4 mr-2" data-id="element-18842" data-mp="3j071e4b8wtm1" />
                You have unsaved changes
              </div>
              <button onClick={handleSaveSettings} className={`flex items-center px-3 py-1.5 rounded-md text-sm font-medium text-white transition-colors ${rt.primaryButton}`} data-id="element-18843" data-mp="3a1lk0chifkmu">
                <SaveIcon className="h-4 w-4 mr-1.5" data-id="element-18844" data-mp="18aqkpao3h3bd" />
                Save Changes
              </button>
            </motion.div>}
        </AnimatePresence>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-18845" data-mp="131aopurlnoae">
          {/* Form */}
          <div className={`p-6 rounded-xl border ${dk.card} space-y-4`} data-id="element-18846" data-mp="1333z9l5c2sno">
            <div data-id="element-18847" data-mp="y1y3tfkagrag">
              <label className={`block text-sm font-medium mb-1 ${dk.textPrimary}`} data-id="element-18848" data-mp="y4kalczcv2rm">
                {config.primaryTimeLabel}
              </label>
              <input type="text" value={settings.averageProductionTime} onChange={e => setSettings({
              ...settings,
              averageProductionTime: e.target.value
            })} className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${dk.inputOnCard} ${rt.focusRing}`} placeholder={config.primaryTimePlaceholder} data-id="element-18849" data-mp="1815fzzfaefvz" />
            </div>
            <div data-id="element-18850" data-mp="kfw0n47f3jz">
              <label className={`block text-sm font-medium mb-1 ${dk.textPrimary}`} data-id="element-18851" data-mp="3squrq3kp1lkg">
                {config.expectationLabel}
              </label>
              <textarea value={settings.currentCustomerExpectation} onChange={e => setSettings({
              ...settings,
              currentCustomerExpectation: e.target.value
            })} className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 min-h-[80px] ${dk.inputOnCard} ${rt.focusRing}`} placeholder={config.expectationPlaceholder} data-id="element-18852" data-mp="3iktny3fenjrr" />
            </div>

            {config.showOrigin && <div data-id="element-18853" data-mp="3regdhbt0rqsi">
                <label className={`block text-sm font-medium mb-1 ${dk.textPrimary}`} data-id="element-18854" data-mp="3rh2l1bfwu6aq">
                  {config.originLabel}
                </label>
                <input type="text" value={settings.shippingOrigin} onChange={e => setSettings({
              ...settings,
              shippingOrigin: e.target.value
            })} className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${dk.inputOnCard} ${rt.focusRing}`} placeholder={config.originPlaceholder} data-id="element-18855" data-mp="2fy9o162ifhbc" />
              </div>}

            {config.showSubscriptionTiming && <div data-id="element-18856" data-mp="7tr6httnrd6z">
                <label className={`block text-sm font-medium mb-1 ${dk.textPrimary}`} data-id="element-18857" data-mp="7q3dqww1eutb">
                  Subscription Timing (Optional)
                </label>
                <input type="text" value={settings.subscriptionTiming || ''} onChange={e => setSettings({
              ...settings,
              subscriptionTiming: e.target.value
            })} className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${dk.inputOnCard} ${rt.focusRing}`} placeholder="e.g., Ships between the 15th and 20th of each month" data-id="element-18858" data-mp="3uz01oqa8x3nd" />
              </div>}

            {config.showQueueFields && <>
                <div data-id="element-18859" data-mp="2af4nz82frunt">
                  <label className={`block text-sm font-medium mb-1 ${dk.textPrimary}`} data-id="element-18860" data-mp="2adjullruyhrj">
                    Queue Status
                  </label>
                  <select value={settings.queueStatus || 'open'} onChange={e => setSettings({
                ...settings,
                queueStatus: e.target.value as 'open' | 'limited' | 'closed'
              })} className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${dk.inputOnCard} ${rt.focusRing}`} data-id="element-18861" data-mp="1hr8u0b494guq">
                    <option value="open" data-id="element-18862" data-mp="2wna5szgwv8e0">Open</option>
                    <option value="limited" data-id="element-18863" data-mp="2woux8g7xib7n">Limited</option>
                    <option value="closed" data-id="element-18864" data-mp="3ankwa1eb6kyn">Closed</option>
                  </select>
                </div>
                <div data-id="element-18865" data-mp="35np2euzbs9wt">
                  <label className={`block text-sm font-medium mb-1 ${dk.textPrimary}`} data-id="element-18866" data-mp="35n6yrdac2tev">
                    Queue Capacity (Optional)
                  </label>
                  <input type="text" value={settings.queueCapacity || ''} onChange={e => setSettings({
                ...settings,
                queueCapacity: e.target.value
              })} className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${dk.inputOnCard} ${rt.focusRing}`} placeholder="e.g., 3 of 5 slots filled" data-id="element-18867" data-mp="3flcz3qghrrhm" />
                </div>
                <div data-id="element-18868" data-mp="pud904fh7bpw">
                  <label className={`block text-sm font-medium mb-1 ${dk.textPrimary}`} data-id="element-18869" data-mp="ptlkteo7kg9n">
                    Booking Window (Optional)
                  </label>
                  <input type="text" value={settings.bookingWindow || ''} onChange={e => setSettings({
                ...settings,
                bookingWindow: e.target.value
              })} className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${dk.inputOnCard} ${rt.focusRing}`} placeholder="e.g., Currently booking for Q2 2024" data-id="element-18870" data-mp="gsvtan5zdc1q" />
                </div>
              </>}
          </div>

          {/* Live Preview */}
          <div data-id="element-18871" data-mp="3kci2b4s9p3yq">
            <h3 className={`text-sm font-medium mb-3 ${dk.textSecondary}`} data-id="element-18872" data-mp="315sd3ov99zj4">
              Live Customer Preview
            </h3>
            <div className="pointer-events-none opacity-90" data-id="element-18873" data-mp="2weao7z6ibpei">
              <FulfillmentTransparencyCard fulfillment={{
              ...currentPublicStatus,
              ...settings // overlay current form state
            }} vendorCategory={vendorCategory} data-id="element-18874" data-mp="2rg8tui3ms3nq" />
            </div>
          </div>
        </div>
      </section>

      {/* Recent Updates Timeline */}
      <section data-id="element-18875" data-mp="3f6cxuqkqe5f9">
        <div className="flex items-center justify-between mb-4" data-id="element-18876" data-mp="3f3qqaqxubpx1">
          <h2 className={`text-lg font-bold ${dk.textHeading}`} data-id="element-18877" data-mp="3a5p4hy915chv">
            Recent Updates
          </h2>
          <button onClick={() => handleOpenComposer()} className={`flex items-center px-3 py-1.5 rounded-md text-sm font-medium text-white transition-colors ${rt.primaryButton}`} data-id="element-18878" data-mp="356ufgekgyrpb">
            <MessageSquareIcon className="h-4 w-4 mr-1.5" data-id="element-18879" data-mp="1tr5q5gjqtglc" />
            Post Update
          </button>
        </div>

        <div className={`p-6 rounded-xl border ${dk.card}`} data-id="element-18880" data-mp="98lhe1h47leh">
          {visibleUpdates.length === 0 ? <div className={`text-center py-8 ${dk.textMuted}`} data-id="element-18881" data-mp="e76lwd3egr8q">
              <MessageSquareIcon className="h-8 w-8 mx-auto mb-2 opacity-50" data-id="element-18882" data-mp="j71bmdbfvas7" />
              <p data-id="element-18883" data-mp="o0dvka56atem">No updates posted yet.</p>
            </div> : <div className="space-y-6 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-gray-200 dark:before:via-gray-700 before:to-transparent" data-id="element-18884" data-mp="syylngqpnwpc">
              {visibleUpdates.map((update, index) => <motion.div key={update.id} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: index * 0.1
          }} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-18885" data-mp="16w4127cbe7x9" data-mpi={index}>
                  <div className={`flex items-center justify-center w-10 h-10 rounded-full border-4 ${dk.isDark ? 'border-gray-800' : 'border-white'} ${getTypeColor(update.type)} shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 shadow-sm z-10`} data-id="element-18886" data-mp="1j8fu9gzsq1pb">
                    {getTypeIcon(update.type)}
                  </div>
                  <div className={`w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4 rounded-xl border shadow-sm ${dk.isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'} ${update.is_pinned ? 'ring-1 ring-purple-500' : ''}`} data-id="element-18887" data-mp="15eojy6e8qdsj">
                    <div className="flex items-center justify-between mb-2" data-id="element-18888" data-mp="2bwtv04i9rc4k">
                      <div className="flex items-center gap-2" data-id="element-18889" data-mp="2jfuri5unk5if">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${getTypeColor(update.type)}`} data-id="element-18890" data-mp="2og8ngpizbp03">
                          {formatType(update.type)}
                        </span>
                        {update.is_pinned && <span className="text-purple-500" title="Pinned Update" data-id="element-18891" data-mp="326ly4zn99ht4">
                            <PinIcon className="h-3.5 w-3.5 fill-current" data-id="element-18892" data-mp="3781g2iu4z87a" />
                          </span>}
                      </div>
                      <span className={`text-xs ${dk.textMuted}`} data-id="element-18893" data-mp="2xf2qaxmwlbm3">
                        {new Date(update.datePosted).toLocaleDateString()}
                        {update.is_edited && ' (edited)'}
                      </span>
                    </div>
                    <p className={`text-sm mb-3 ${dk.textPrimary}`} data-id="element-18894" data-mp="1lcoisz6ifbi9">
                      {update.message}
                    </p>
                    <div className={`text-xs space-y-1 ${dk.textSecondary}`} data-id="element-18895" data-mp="1qag9xqzbytle">
                      <p data-id="element-18896" data-mp="1bn50bumpljnx">
                        <span className="font-medium" data-id="element-18897" data-mp="1bltttn1vpxt4">Scope:</span>{' '}
                        {update.affectedScope}
                      </p>
                      {update.estimatedNewTimeline && <p data-id="element-18898" data-mp="12l44n274j5od">
                          <span className="font-medium" data-id="element-18899" data-mp="12o01en78job8">Timeline:</span>{' '}
                          {update.estimatedNewTimeline}
                        </p>}
                    </div>

                    {/* Actions */}
                    <div className={`mt-4 pt-3 border-t ${dk.divider} flex items-center gap-3`} data-id="element-18900" data-mp="xm0wh8vukjc6">
                      <button onClick={() => handleOpenComposer(update)} className={`text-xs font-medium flex items-center hover:${rt.textPrimary} ${dk.textSecondary} transition-colors`} data-id="element-18901" data-mp="2n2jsml3z3ncp">
                        <Edit2Icon className="h-3 w-3 mr-1" data-id="element-18902" data-mp="bf6l5p27crgs" /> Edit
                      </button>
                      <button onClick={() => handleDeleteUpdate(update.id)} className={`text-xs font-medium flex items-center hover:text-red-500 ${dk.textSecondary} transition-colors`} data-id="element-18903" data-mp="q3kw6n9rvm8f">
                        <Trash2Icon className="h-3 w-3 mr-1" data-id="element-18904" data-mp="z5u5j9tb19k8" /> Delete
                      </button>
                      {update.type !== 'resolved' && <button onClick={() => handleOpenComposer(update, true)} className={`text-xs font-medium flex items-center hover:text-green-500 ${dk.textSecondary} transition-colors ml-auto`} data-id="element-18905" data-mp="190uh68uqx7rs">
                          <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-18906" data-mp="35v8ryfd1v8pn" /> Mark
                          Resolved
                        </button>}
                    </div>
                  </div>
                </motion.div>)}
            </div>}
        </div>
      </section>

      {/* Composer Modal */}
      <AnimatePresence data-id="element-18907" data-mp="1iqo40tmxivj9">
        {isComposerOpen && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6" data-id="element-18908" data-mp="1nrc4e5x1soq5">
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsComposerOpen(false)} data-id="element-18909" data-mp="1sqor8qnx5vsq" />
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className={`relative w-full max-w-lg rounded-xl shadow-xl border overflow-hidden ${dk.card}`} data-id="element-18910" data-mp="26jec2rrpouo9">
              <div className={`px-6 py-4 border-b ${dk.divider} flex items-center justify-between`} data-id="element-18911" data-mp="2cjwstafv90z0">
                <h3 className={`text-lg font-bold ${dk.textHeading}`} data-id="element-18912" data-mp="39afkuq1bevxq">
                  {editingUpdateId ? 'Edit Update' : composerData.type === 'resolved' ? 'Mark as Resolved' : 'Post New Update'}
                </h3>
                <button onClick={() => setIsComposerOpen(false)} className={`p-1 rounded-md hover:${dk.surfaceMuted} ${dk.textMuted}`} data-id="element-18913" data-mp="2vhpw4duejcwx">
                  <XIcon className="h-5 w-5" data-id="element-18914" data-mp="3n9owdcekt1vd" />
                </button>
              </div>
              <div className="p-6 space-y-4" data-id="element-18915" data-mp="37re5imh6kd6z">
                <div data-id="element-18916" data-mp="37qm8r8buk9f4">
                  <label className={`block text-sm font-medium mb-1 ${dk.textPrimary}`} data-id="element-18917" data-mp="37ua3ubwxx3vy">
                    Update Type
                  </label>
                  <select value={composerData.type} onChange={e => setComposerData({
                ...composerData,
                type: e.target.value as VendorUpdate['type']
              })} className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${dk.inputOnCard} ${rt.focusRing}`} disabled={!!composerData.resolved_from_update_id} data-id="element-18918" data-mp="2ytc07uqeyydw">
                    <option value="production-update" data-id="element-18919" data-mp="dc568zd48phb">Production Update</option>
                    <option value="shipping-update" data-id="element-18920" data-mp="da2by34xka6s">Shipping Update</option>
                    <option value="delay" data-id="element-18921" data-mp="i3wqks5ohyz0">Delay Notice</option>
                    <option value="issue" data-id="element-18922" data-mp="22rz4tv71oycj">Issue Report</option>
                    <option value="resolved" data-id="element-18923" data-mp="1xtxkz81sov00">Resolved</option>
                  </select>
                </div>
                <div data-id="element-18924" data-mp="2cikb84syn760">
                  <label className={`block text-sm font-medium mb-1 ${dk.textPrimary}`} data-id="element-18925" data-mp="2cj3mj2o3uxb2">
                    Message
                  </label>
                  <textarea value={composerData.message} onChange={e => setComposerData({
                ...composerData,
                message: e.target.value
              })} className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 min-h-[100px] ${dk.inputOnCard} ${rt.focusRing}`} placeholder={config.composerMessagePlaceholder} data-id="element-18926" data-mp="1jtmu450hjgeu" />
                </div>
                <div data-id="element-18927" data-mp="3084wiucl6gig">
                  <label className={`block text-sm font-medium mb-1 ${dk.textPrimary}`} data-id="element-18928" data-mp="30ar1qnc08jxi">
                    Affected Scope
                  </label>
                  <input type="text" value={composerData.affectedScope} onChange={e => setComposerData({
                ...composerData,
                affectedScope: e.target.value
              })} className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${dk.inputOnCard} ${rt.focusRing}`} placeholder={config.scopePlaceholder} data-id="element-18929" data-mp="3ccsdh7yrc748" />
                </div>
                {composerData.type !== 'resolved' && <div data-id="element-18930" data-mp="25un8nrlbo50n">
                    <label className={`block text-sm font-medium mb-1 ${dk.textPrimary}`} data-id="element-18931" data-mp="25vezyq7vzgl4">
                      Estimated New Timeline (Optional)
                    </label>
                    <input type="text" value={composerData.estimatedNewTimeline || ''} onChange={e => setComposerData({
                ...composerData,
                estimatedNewTimeline: e.target.value
              })} className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 ${dk.inputOnCard} ${rt.focusRing}`} placeholder={config.timelinePlaceholder} data-id="element-18932" data-mp="rxarfg59v1ok" />
                  </div>}
                <div className="flex items-center mt-2" data-id="element-18933" data-mp="5bouaxml0my">
                  <input type="checkbox" id="pin-update" checked={composerData.is_pinned || false} onChange={e => setComposerData({
                ...composerData,
                is_pinned: e.target.checked
              })} className={`h-4 w-4 rounded border-gray-300 ${rt.textPrimary} focus:ring-${rt.textPrimary}`} data-id="element-18934" data-mp="3res6exx77uye" />
                  <label htmlFor="pin-update" className={`ml-2 block text-sm ${dk.textSecondary}`} data-id="element-18935" data-mp="105dhbkl0gpfg">
                    Pin to top of updates feed
                  </label>
                </div>
              </div>
              <div className={`px-6 py-4 border-t ${dk.divider} flex justify-end gap-3 ${dk.isDark ? 'bg-gray-800/50' : 'bg-gray-50'}`} data-id="element-18936" data-mp="v7byn0rhys4i">
                <button onClick={() => setIsComposerOpen(false)} className={`px-4 py-2 rounded-md text-sm font-medium border transition-colors ${dk.isDark ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`} data-id="element-18937" data-mp="mervpo46a1dv">
                  Cancel
                </button>
                <button onClick={handleSaveUpdate} className={`px-4 py-2 rounded-md text-sm font-medium text-white transition-colors ${rt.primaryButton}`} data-id="element-18938" data-mp="3p0osx6rm2xqw">
                  {editingUpdateId ? 'Save Changes' : 'Post Update'}
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
};